import React, { useCallback, useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../reducers";
import { sortFilterItems } from "../menu/products/Filters";
import IMenuItemBody from "../../interfaces/IMenuItemBody";
import {
    replaceSelectedList,
    setFilterState, setItemList,
    setSelectedListId, TaxMenuItemReducer
} from './state';
import { TaxCheckBox } from "./ui/TaxCheckBox";
import { TaxesControls } from "./ui/TaxesControls";
import { ITaxesMenuItemState } from "./types";
import { TaxesList } from "./ui/TaxesList";
import Styles from '../../scss/taxes.module.scss';
import { Button } from "@material-ui/core";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import { IDialogChildComponent } from "../../reducers/dialog";
import { useTranslation } from "react-i18next";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { APIExtensions, ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import APIDefintions from '../../yoello-lib/modules/API2/APIDefs';
import { ITaxes } from "../../interfaces/ITaxes";
import { setTaxName } from "./state/TaxesMenuItems.actions";

const initalState: ITaxesMenuItemState = {
    itemList: [],
    selectedList: [],
    filterState: null,
    filterList: null,
    searchText: "",
    taxName: null
};
export interface ITaxesItems extends IDialogChildComponent {
    taxName: string;
    id: string;
    isVenueOpen: boolean;
    callTaxesAPI: ICallAPIFunction<ITaxes>;
}
interface ITaxesMenuItemsProps extends ITaxesItems {
    taxType: "menu_items" | "venue_fees";
    itemFilterState?: any[] | null;
    APIDefintion: APIExtensions;
    emptyListTitle: string;
    emptyListInfo?: string;
    columns: any;
    callTaxesAPI: ICallAPIFunction<ITaxes>;
}

export function TaxesItems({
    taxName,
    id,
    onClose,
    taxType,
    itemFilterState,
    APIDefintion,
    emptyListTitle,
    emptyListInfo,
    columns,
    isVenueOpen,
    callTaxesAPI
}: ITaxesMenuItemsProps) {
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const [state, dispatch] = useReducer(
        TaxMenuItemReducer,
        initalState
    );
    const query = id + `/${taxType}`;
    const { t } = useTranslation();
    const { itemList, selectedList, filterState, filterList, searchText, taxName: taxNameSet } = state;
    const { apiData, isLoading, isError } = useAPIV2<IMenuItemBody[]>(APIDefintion,
        { yoelloAPIParams: { venue_id: selectedVenueId, size: 200, include_disabled: true }, isPaginated: true });
    const { apiData: selectedItems, isLoading: isSelectedLoading, callAPI, isError: isSelectedError } = useAPIV2<IMenuItemBody[]>(APIDefintions.taxes, {
        query,
        yoelloAPIParams: { venue_id: selectedVenueId, size: 200, include_disabled: true },
        isPaginated: true
    });
    useEffect(() => {
        if (!taxName) {
            /**Get the tax name if doesn't have */
            callTaxesAPI(HTTPMethods.GET, { query: id }).then(({ isSuccessful, data }) => {
                if (isSuccessful) {
                    dispatch(setTaxName(data?.tax_band_name ?? null));
                }
            })
        } else {
            dispatch(setTaxName(taxName));
        }
        //eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (apiData && !isError) {
            dispatch(setItemList(apiData as IMenuItemBody[]));
        }
        //eslint-disable-next-line
    }, [apiData]);
    useEffect(() => {
        if (!isSelectedError && selectedItems && Array.isArray(selectedItems)) {
            dispatch(replaceSelectedList(selectedItems as IMenuItemBody[]));
        }
        //eslint-disable-next-line
    }, [selectedItems]);
    useEffect(() => {
        if (itemFilterState) {
            dispatch(setFilterState(itemFilterState));
        }
    }, [itemFilterState]);
    async function onSubmit() {
        await callAPI(HTTPMethods.DELETE, {
            query,
        });
        await callAPI(HTTPMethods.PUT, {
            body: selectedList,
            query,
            successCall: "Applied taxes to items"
        });
        await callAPI(HTTPMethods.GET, { query });
        onClose();
    }
    function setSelectedList(menuItem: IMenuItemBody) {
        if (!isVenueOpen) {
            dispatch(setSelectedListId(menuItem));
        }
    }
    const menuItemList = useCallback(() => {
        let list = itemList;

        if (searchText && searchText?.length > 0 && taxType === 'menu_items') {
            const text = searchText.toLowerCase();
            list = list.filter((item: IMenuItemBody) => item.name_public ? item.name_public.toLowerCase().includes(text) : false);
        }
        if (searchText && searchText?.length > 0 && taxType === 'venue_fees') {
            const text = searchText.toLowerCase();
            list = list.filter((item: IMenuItemBody) => item.fee_type ? item.fee_type.toLowerCase().includes(text) : false);
        }
        if (filterState && filterList) {
            list = sortFilterItems(filterList, filterState, list);
        }
        return list.map((menuItem: IMenuItemBody) => {
            let checked = false;
            const menuItemId = menuItem?.id!;
            if (selectedList.length > 0 &&
                selectedList.findIndex((listItem: IMenuItemBody) => listItem.id === menuItemId) > -1) {
                checked = true;
            }
            return {
                ...menuItem,
                selection: <TaxCheckBox checked={checked} />,
                enabled: menuItem.enabled ? 'Yes' : "No",
            };
        });
    }, [itemList, searchText, taxType, filterState, filterList, selectedList]);

    return <>
        <div className={Styles.MenuItemListContainer}>

            <TaxesControls {...state} dispatch={dispatch} taxName={taxNameSet} />

            <div className={Styles.TaxList}>
                <TaxesList
                    isLoading={isLoading || isSelectedLoading}
                    itemList={menuItemList()}
                    columns={columns}
                    listHeight={200}
                    className={Styles.MenuItemTaxCard}
                    itemSize={50}
                    emptyListTitle={emptyListTitle}
                    emptyListInfo={emptyListInfo}
                    onItemSelection={setSelectedList}
                    maxListLength={4}
                />

            </div>
            <div className={Styles.MenuBottomBar}>
                <Button
                    variant="contained"
                    color="primary"
                    data-component-name="CREATE_NEW"
                    size="large"
                    disabled={isVenueOpen || isSelectedLoading}
                    onClick={onSubmit}>
                    {taxType === 'menu_items' ? t('taxes.buttons.apply') : t('taxes.buttons.applyFees')}
                </Button>
                <Button
                    size="large"
                    variant="contained"
                    onClick={onClose}
                >
                    {t('delete.cancel')}
                </Button>

            </div>
        </div>

    </>;
}


